import type {
  DecodedShare,
  LocalLaneForShare,
  AppConfig,
  CalendarColor,
  CalendarFeed,
  DisplayEvent,
  FeedCategory,
  FeedValidators,
  FindReplaceRule,
  FontSize,
  PaletteFlavor,
  ParsedEvent,
  ResolvedScheme,
  StyleVariant,
  SubLanes,
  Timezone,
  Zoom,
} from './types';
import { DEFAULT_FONT_SIZE, DEFAULT_PALETTE_FLAVOR, FONT_SIZES, PALETTE_FLAVORS, RESOLVED_SCHEMES, SCRATCHPAD_FEED_ID } from './types';
import { detent } from './haptics';
import { pickRandom } from './shake';
import { loadConfig, defaultConfig } from './storage';
import { applyRules } from './rules';
import { mergeConsecutiveDays } from './event-display';
import { rangeForToday, overlapsWindow } from './layout';
import { today } from './today.svelte';
import {
  loadScratchpad,
  saveScratchpad,
  clearScratchpad,
  makeScratchpadEvent,
  newUid,
  reviseEvent,
  type ScratchpadInput,
} from './scratchpad';
import { MS_PER_DAY } from './time';
import { rescheduled, type DragChange } from './event-drag';
import { expandLaneEvents, moveExdates, occurrenceUid, splitOccurrenceUid } from './recurrence';
import { pushUndo, popUndo, clearUndo, noteUndone, noteUndoStale, invalidateUndoFor, type UndoEntry } from './undo.svelte';
import { describeReschedule } from './undo-label';

export const config = $state<AppConfig>(loadConfig());

export const events = $state<{
  byFeed: Record<string, ParsedEvent[]>;
  tzByFeed: Record<string, string>;
  // The raw feed text of the last successful fetch, session-only, for the
  // event modal's on-demand source view (one string per feed — much lighter
  // than the per-occurrence VEVENT copies this replaced).
  rawTextByFeed: Record<string, string>;
  lastSuccessAt: Record<string, number>;
  // Per-feed ETag/Last-Modified, so refreshes can revalidate instead of
  // re-downloading and re-parsing an unchanged feed.
  validators: Record<string, FeedValidators>;
}>({ byFeed: loadLocalLanes(), tzByFeed: {}, rawTextByFeed: {}, lastSuccessAt: {}, validators: {} });

// The timeline's visible window [start, end], derived from today + the configured
// past/future month limits. Single source of truth for both the render window
// (Timeline geometry, the display-pipeline window filter below) and the feed
// parse window (App.loadAllFeeds), so the two can never drift. Exposed via a
// getter because Svelte forbids exporting `$derived` state directly.
const _displayRange = $derived(
  rangeForToday(today.value, {
    pastMonths: config.pastMonths,
    futureMonths: config.futureMonths,
  }),
);
export function displayRange(): { start: Date; end: Date } {
  return _displayRange;
}

// The lane id stored inside a scratchpad FeedSource, derived from its feed id.
function laneIdOf(feedId: string): string {
  return feedId.startsWith('scratchpad:') ? feedId.slice('scratchpad:'.length) : 'default';
}

// Local lanes edited since they were last exported as .ics in THIS session —
// drives the "changed since last export" dot in Settings → Calendars. Session
// only on purpose: an export is a download we can't observe afterwards, so a
// persisted flag would claim knowledge about a file we lost track of the moment
// the page closed; starting each session clean is the honest default.
export const laneExport = $state<{ dirty: Record<string, true> }>({ dirty: {} });

export function markLaneExported(feedId: string): void {
  delete laneExport.dirty[feedId];
}

// Persist a local lane after an edit, and flag it as changed since its export.
function persistLane(feedId: string, keepUndo = false): void {
  // Any write but a reschedule's or an undo's outdates that lane's undo history.
  if (!keepUndo) invalidateUndoFor(feedId);
  saveScratchpad(events.byFeed[feedId] ?? [], laneIdOf(feedId));
  laneExport.dirty[feedId] = true;
}

// Hydrate every local lane (the Draft plus any imported .ics) from localStorage.
function loadLocalLanes(): Record<string, ParsedEvent[]> {
  const out: Record<string, ParsedEvent[]> = {};
  for (const f of config.feeds) {
    if (f.source.kind === 'scratchpad') out[f.id] = loadScratchpad(f.source.id ?? 'default');
  }
  if (!out[SCRATCHPAD_FEED_ID]) out[SCRATCHPAD_FEED_ID] = loadScratchpad();
  return out;
}

// Find which local lane currently holds an event, so edits/deletes route to it.
// An occurrence of a repeating event resolves to its series.
function laneFeedIdOf(uid: string): string {
  const id = storedUidOf(uid);
  for (const f of config.feeds) {
    if (f.source.kind !== 'scratchpad') continue;
    if ((events.byFeed[f.id] ?? []).some((e) => e.uid === id)) return f.id;
  }
  return SCRATCHPAD_FEED_ID;
}

// The stored local event a display uid stands for: itself, or — for an
// occurrence (`series#r<ms>`) of a repeating event — its series.
function storedUidOf(uid: string): string {
  const occ = splitOccurrenceUid(uid);
  if (!occ) return uid;
  for (const f of config.feeds) {
    if (f.source.kind !== 'scratchpad') continue;
    if ((events.byFeed[f.id] ?? []).some((e) => e.uid === occ.seriesUid && e.rrule)) return occ.seriesUid;
  }
  return uid;
}

// The occurrence a display uid names, when it is one of a stored series.
function occurrenceOf(uid: string): { feedId: string; series: ParsedEvent; startMs: number } | null {
  const occ = splitOccurrenceUid(uid);
  if (!occ) return null;
  for (const f of config.feeds) {
    if (f.source.kind !== 'scratchpad') continue;
    const series = (events.byFeed[f.id] ?? []).find((e) => e.uid === occ.seriesUid && e.rrule);
    if (series) return { feedId: f.id, series, startMs: occ.startMs };
  }
  return null;
}

/** The stored local event behind a display uid (a repeating event's series), for editing. */
export function localEventForEdit(uid: string): ParsedEvent | null {
  const id = storedUidOf(uid);
  for (const f of config.feeds) {
    if (f.source.kind !== 'scratchpad') continue;
    const hit = (events.byFeed[f.id] ?? []).find((e) => e.uid === id);
    if (hit) return hit;
  }
  return null;
}

export function addScratchpadEvent(
  input: ScratchpadInput,
  feedId: string = SCRATCHPAD_FEED_ID,
): ParsedEvent {
  const ev = makeScratchpadEvent(input);
  ev.feedId = feedId;
  const prev = events.byFeed[feedId] ?? [];
  events.byFeed[feedId] = [...prev, ev].sort((a, b) => a.start.getTime() - b.start.getTime());
  persistLane(feedId);
  // A newly created event must be visible — un-hide its lane (e.g. adding via a
  // 1W empty slot targets the Draft, which may be disabled).
  setFeedHidden(feedId, false);
  return ev;
}

// Editing an occurrence of a repeating event edits the whole series.
export function updateScratchpadEvent(uid: string, input: ScratchpadInput): void {
  const feedId = laneFeedIdOf(uid);
  const id = storedUidOf(uid);
  const prev = events.byFeed[feedId] ?? [];
  const fresh = makeScratchpadEvent(input);
  fresh.feedId = feedId;
  events.byFeed[feedId] = prev
    // Keeps the uid and bumps SEQUENCE / LAST-MODIFIED (see reviseEvent).
    // The editor has no STATUS or URL field, so an imported cancelled event
    // stays that way through an edit and keeps its link.
    .map((e) =>
      e.uid === id
        ? reviseEvent(e, {
            ...fresh,
            ...(e.cancelled ? { cancelled: true } : {}),
            ...(e.url ? { url: e.url } : {}),
            ...(fresh.rrule ? keptExdates(e, fresh) : {}),
          })
        : e,
    )
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  persistLane(feedId);
}

// A series still repeating after an edit keeps its skipped days, moved along
// with its start on its wall clock (a 10:00 → 11:00 edit keeps the same days
// skipped, across DST too).
function keptExdates(prev: ParsedEvent, next: ParsedEvent): { exdates?: Date[] } {
  if (!prev.rrule || !prev.exdates?.length) return {};
  return { exdates: moveExdates(prev, next) };
}

// Deleting from the editor removes the whole event — every occurrence of a
// repeating one (the tray's delete removes single occurrences).
export function deleteScratchpadEvent(uid: string): void {
  const feedId = laneFeedIdOf(uid);
  const id = storedUidOf(uid);
  const prev = events.byFeed[feedId] ?? [];
  events.byFeed[feedId] = prev.filter((e) => e.uid !== id);
  persistLane(feedId);
  const gone = (u: string): boolean => u === id || splitOccurrenceUid(u)?.seriesUid === id;
  if ([...selection.uids].some(gone)) {
    const next = new Set([...selection.uids].filter((u) => !gone(u)));
    selection.uids = next;
    if (next.size === 0) selection.mode = false;
  }
  if (ui.modalEvent && gone(ui.modalEvent.uid)) ui.modalEvent = null;
}

// Move one or more events between local lanes, keeping their uids. URL/secret
// feeds are network-fetched and overwritten on refresh, so only scratchpad lanes
// are valid targets. Every touched lane is re-sorted and persisted once.
// Returns a uid→original-lane map of the events it actually moved, so callers can
// reverse the move (e.g. an undo affordance) by moving each back to its source.
export function moveEventsToLane(uids: Iterable<string>, destFeedId: string): Map<string, string> {
  const moved = new Map<string, string>();
  if (!destFeedId.startsWith('scratchpad:')) return moved;
  const touched = new Set<string>([destFeedId]);
  // An occurrence moves its whole series (once, however many are selected).
  for (const uid of new Set([...uids].map(storedUidOf))) {
    const srcFeedId = laneFeedIdOf(uid);
    if (srcFeedId === destFeedId) continue;
    const ev = (events.byFeed[srcFeedId] ?? []).find((e) => e.uid === uid);
    if (!ev) continue;
    events.byFeed[srcFeedId] = (events.byFeed[srcFeedId] ?? []).filter((e) => e.uid !== uid);
    events.byFeed[destFeedId] = [...(events.byFeed[destFeedId] ?? []), { ...ev, feedId: destFeedId }];
    touched.add(srcFeedId);
    moved.set(uid, srcFeedId);
    if (ui.modalEvent && storedUidOf(ui.modalEvent.uid) === uid) ui.modalEvent = { ...ui.modalEvent, feedId: destFeedId };
  }
  for (const feedId of touched) {
    events.byFeed[feedId] = (events.byFeed[feedId] ?? []).sort(
      (a, b) => a.start.getTime() - b.start.getTime(),
    );
    persistLane(feedId);
  }
  return moved;
}

// Move a single event — thin wrapper over the batch move.
export function moveEventToLane(uid: string, destFeedId: string): void {
  moveEventsToLane([uid], destFeedId);
}

// Delete only the local-lane events among the given uids; URL/secret-feed events
// are left alone (they re-fetch). An occurrence of a repeating event is skipped
// (an EXDATE on its series) rather than deleting the series. Each touched lane
// is persisted once.
export function deleteLocalEvents(uids: Iterable<string>): void {
  const drop = new Set<string>();
  const skip = new Map<string, number[]>(); // series uid → occurrence starts
  for (const uid of uids) {
    const occ = occurrenceOf(uid);
    if (occ) skip.set(occ.series.uid, [...(skip.get(occ.series.uid) ?? []), occ.startMs]);
    else drop.add(uid);
  }
  const touched: string[] = [];
  for (const f of config.feeds) {
    if (f.source.kind !== 'scratchpad') continue;
    const list = events.byFeed[f.id] ?? [];
    let changed = false;
    const next = list.flatMap((e) => {
      if (drop.has(e.uid)) {
        changed = true;
        return [];
      }
      const starts = skip.get(e.uid);
      if (!starts) return [e];
      changed = true;
      return [withExdates(e, starts)];
    });
    if (changed) {
      events.byFeed[f.id] = next;
      touched.push(f.id);
    }
  }
  for (const id of touched) persistLane(id);
}

// `series` with the given occurrence starts skipped (a new revision).
function withExdates(series: ParsedEvent, starts: number[]): ParsedEvent {
  const have = new Set((series.exdates ?? []).map((d) => d.getTime()));
  const add = starts.filter((ms) => !have.has(ms));
  if (add.length === 0) return series;
  const exdates = [...(series.exdates ?? []), ...add.map((ms) => new Date(ms))].sort(
    (a, b) => a.getTime() - b.getTime(),
  );
  return reviseEvent(series, { ...series, exdates });
}

// Re-time local-lane events by a drag / Alt+arrow `change` (see event-drag.ts),
// applied on the wall clock of `tz` so timed events keep their clock time across
// a DST boundary. URL/secret-feed events are skipped (they re-fetch). Each touched
// lane is re-sorted and persisted once. Returns how many events moved.
// An occurrence of a repeating event comes out as a one-off with a new uid;
// `onRename(occurrenceUid, newUid)` reports it, so a caller holding focus on it
// can follow it.
export function rescheduleLocalEvents(
  uids: Iterable<string>,
  change: DragChange,
  tz: Timezone = config.timezone,
  onRename?: (from: string, to: string) => void,
): number {
  const want = new Set<string>();
  // An occurrence of a repeating event moves on its own: the series skips that
  // day and the moved copy becomes a one-off event (new uid) in the same lane.
  const detach = new Map<string, number[]>(); // series uid → occurrence starts
  for (const uid of uids) {
    const occ = occurrenceOf(uid);
    if (occ) detach.set(occ.series.uid, [...(detach.get(occ.series.uid) ?? []), occ.startMs]);
    else want.add(uid);
  }
  let count = 0;
  const renames = new Map<string, string>(); // occurrence uid → its one-off's uid
  const lanes: UndoEntry['lanes'] = [];
  const changed: ParsedEvent[] = [];
  for (const f of config.feeds) {
    if (f.source.kind !== 'scratchpad') continue;
    const list = events.byFeed[f.id] ?? [];
    if (!list.some((e) => want.has(e.uid) || detach.has(e.uid))) continue;
    const added: ParsedEvent[] = [];
    events.byFeed[f.id] = list
      .map((e) => {
        const starts = detach.get(e.uid);
        if (starts) {
          const dur = e.end.getTime() - e.start.getTime();
          for (const ms of starts) {
            count++;
            const one = makeScratchpadEvent({
              title: e.title, start: new Date(ms), end: new Date(ms + dur), allDay: e.allDay,
              location: e.location, description: e.description, category: e.category,
            });
            renames.set(occurrenceUid(e.uid, ms), one.uid);
            added.push({
              ...rescheduled({ ...one, feedId: f.id }, change, tz),
              ...(e.url ? { url: e.url } : {}),
              ...(e.cancelled ? { cancelled: true } : {}),
            });
          }
          return withExdates(e, starts);
        }
        if (!want.has(e.uid)) return e;
        count++;
        const next = reviseEvent(e, rescheduled(e, change, tz));
        changed.push(next);
        return next;
      })
      .concat(added)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    changed.push(...added);
    persistLane(f.id, true);
    // Read back, so `after` is the very reference a later identity check sees.
    lanes.push({ feedId: f.id, before: list, after: events.byFeed[f.id]! });
  }
  if (count > 0) {
    pushUndo({
      label: describeReschedule(change, changed, config),
      lanes,
      renames,
    });
  }
  if (renames.size) {
    // The old occurrence ids are gone: carry the selection over to the one-offs
    // (or tray Move/Copy would resolve them back to the whole series), and tell
    // the caller so it can move its focus.
    applyUidRenames(renames);
    for (const [from, to] of renames) onRename?.(from, to);
  }
  return count;
}

// Event uids that stopped existing under a new name (a repeat occurrence
// detached as a one-off, or an undo joining it back): carry the selection over,
// drop an open card on an old uid, and tell views that track their own focus by
// uid (1W's WeekGrid) to follow.
function applyUidRenames(renames: Map<string, string>): void {
  if ([...selection.uids].some((u) => renames.has(u))) {
    selection.uids = new Set([...selection.uids].map((u) => renames.get(u) ?? u));
  }
  if (ui.modalEvent && renames.has(ui.modalEvent.uid)) ui.modalEvent = null;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('cal:uids-renamed', { detail: { renames } }));
  }
}

// Revert the latest reschedule (drag, resize, Alt+arrow nudge): its lanes go
// back to the arrays they held before it. Refused — and the history dropped —
// when any of those lanes was written since, so a later edit is never lost.
export function undoLastChange(): boolean {
  const entry = popUndo();
  if (!entry) return false;
  if (entry.lanes.some((l) => events.byFeed[l.feedId] !== l.after)) {
    clearUndo();
    noteUndoStale();
    return true;
  }
  // Timeline focus is an index into the lane's sorted list, which the restore
  // reorders: note the focused event's uid to find it again afterwards.
  const touched = entry.lanes.some((l) => l.feedId === focus.feedId);
  const focusedUid =
    touched && focus.feedId && focus.eventIndex >= 0
      ? timelineEventsFor(focus.feedId)[focus.eventIndex]?.uid
      : undefined;
  for (const l of entry.lanes) {
    events.byFeed[l.feedId] = l.before;
    persistLane(l.feedId, true);
  }
  // The detached one-offs are gone again: map everything back to the occurrences.
  const back = new Map([...entry.renames].map(([from, to]) => [to, from]));
  if (back.size) applyUidRenames(back);
  if (touched) {
    focus.eventIndex = -1;
    if (focusedUid) focusEventByUid(back.get(focusedUid) ?? focusedUid);
  }
  noteUndone(entry.label);
  return true;
}

// Copy the given events (found in any lane/feed) into a local lane as fresh
// scratchpad events (new uids). Used for URL-only selections where move isn't
// possible. Originals are left intact.
// Returns the uids of the freshly created copies, so callers can reverse the copy
// (e.g. an undo affordance) by deleting them.
export function copyEventsToLane(uids: Iterable<string>, destFeedId: string): string[] {
  if (!destFeedId.startsWith('scratchpad:')) return [];
  // An occurrence of a local repeating event copies its whole series.
  const want = new Set([...uids].map(storedUidOf));
  const copies: ParsedEvent[] = [];
  for (const list of Object.values(events.byFeed)) {
    for (const e of list) {
      if (!want.has(e.uid)) continue;
      const c = makeScratchpadEvent({
        title: e.title, start: e.start, end: e.end, allDay: e.allDay,
        location: e.location, description: e.description, category: e.category,
        rrule: e.rrule, tzid: e.tzid,
      });
      c.feedId = destFeedId;
      if (e.rrule && e.exdates?.length) c.exdates = e.exdates;
      if (e.url) c.url = e.url;
      if (e.cancelled) c.cancelled = true;
      copies.push(c);
    }
  }
  if (copies.length === 0) return [];
  events.byFeed[destFeedId] = [...(events.byFeed[destFeedId] ?? []), ...copies].sort(
    (a, b) => a.start.getTime() - b.start.getTime(),
  );
  persistLane(destFeedId);
  return copies.map((c) => c.uid);
}

function newLaneId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Create a new local lane (behaving like the Draft) from imported .ics events,
// or from a shared local feed (which carries its own category/timezone).
export function createImportedLane(
  name: string,
  evts: ParsedEvent[],
  opts?: {
    category?: FeedCategory;
    timezone?: string;
    style?: StyleVariant;
    color?: CalendarColor;
    hidden?: boolean;
    subLanes?: SubLanes;
  },
): CalendarFeed {
  const id = newLaneId();
  const feedId = 'scratchpad:' + id;
  const order = config.feeds.reduce((m, f) => Math.max(m, f.order), -1) + 1;
  const feed: CalendarFeed = {
    id: feedId,
    source: { kind: 'scratchpad', id },
    name,
    collapsed: false,
    order,
    kind: 'events',
    category: opts?.category ?? 'none',
    ...(opts?.style ? { style: opts.style } : {}),
    ...(opts?.color ? { color: opts.color } : {}),
    ...(opts?.timezone ? { timezone: opts.timezone } : {}),
    ...(opts?.hidden ? { hidden: true } : {}),
    ...(opts?.subLanes && opts.subLanes !== 'none' ? { subLanes: opts.subLanes } : {}),
  };
  // Events are looked up and selected by uid, so a uid already in use — by
  // another lane (the same file imported twice), a subscribed feed carrying
  // the same events, or an earlier event of this same import — would tie the
  // copies together, every edit, skipped day or selection hitting both. Such
  // an event gets a fresh uid: it's a new copy.
  const taken = new Set<string>();
  for (const list of Object.values(events.byFeed)) for (const e of list) taken.add(e.uid);
  const laneEvents = evts
    .map((e) => {
      const uid = taken.has(e.uid) ? newUid() : e.uid;
      taken.add(uid);
      return { ...e, feedId, uid };
    })
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  config.feeds = [...config.feeds, feed];
  events.byFeed[feedId] = laneEvents;
  saveScratchpad(laneEvents, id);
  return feed;
}

// The local (scratchpad) lanes paired with their live reactive events — read
// synchronously by the share buttons so editing Draft events re-triggers the
// share-link recompute. Empty lanes are dropped, except an empty-but-enabled
// Draft (so its enabled state still travels); the encoder filters the rest.
export function localLanesForShare(): LocalLaneForShare[] {
  const out: LocalLaneForShare[] = [];
  for (const feed of config.feeds) {
    if (feed.source.kind !== 'scratchpad') continue;
    const evts = events.byFeed[feed.id] ?? [];
    const keepEmptyDraft = feed.id === SCRATCHPAD_FEED_ID && !feed.hidden;
    if (evts.length === 0 && !keepEmptyDraft) continue;
    out.push({ feed, events: evts });
  }
  return out;
}

// After a config import replaced the feed list: load each local lane from the
// export file when it carried one (written to storage as-is, so it counts as
// exported), else from this device's storage, as before files carried lanes.
export function restoreLocalLanes(lanes: Record<string, ParsedEvent[]>): void {
  for (const feed of config.feeds) {
    if (feed.source.kind !== 'scratchpad') continue;
    const laneId = laneIdOf(feed.id);
    const fromFile = lanes[feed.id];
    if (fromFile) {
      saveScratchpad(fromFile, laneId);
      markLaneExported(feed.id);
    }
    events.byFeed[feed.id] = fromFile ?? loadScratchpad(laneId);
  }
}

// Append events to a local lane (keeping its uids), re-sort, and persist. Used to
// merge a shared Draft into the recipient's own Draft on import.
export function addEventsToLane(feedId: string, evts: ParsedEvent[]): void {
  if (!feedId.startsWith('scratchpad:')) return;
  const stamped = evts.map((e) => ({ ...e, feedId }));
  events.byFeed[feedId] = [...(events.byFeed[feedId] ?? []), ...stamped].sort(
    (a, b) => a.start.getTime() - b.start.getTime(),
  );
  persistLane(feedId);
}

// Set (or clear) a feed's hidden/enabled state in place.
export function setFeedHidden(feedId: string, hidden: boolean): void {
  const feed = config.feeds.find((f) => f.id === feedId);
  if (!feed) return;
  if (hidden) feed.hidden = true;
  else delete feed.hidden;
}

// Purge a local lane's stored events (the caller removes it from config.feeds).
export function removeLocalLane(feedId: string): void {
  clearScratchpad(laneIdOf(feedId));
  delete events.byFeed[feedId];
  delete laneExport.dirty[feedId];
}

// Empty the Draft lane (in memory + storage). The Draft feed itself stays in the
// default config; only its events are dropped. Used by the dev-mode reset so
// seeded test data doesn't survive into a "clean" reset.
export function clearDraftLane(): void {
  clearScratchpad();
  events.byFeed[SCRATCHPAD_FEED_ID] = [];
}

// Developer/test helper: build a demo config — a spread of Draft events, an extra
// imported lane, and demo filters covering every rule mode (matte / replace /
// block) — and hand it to the share-import dialog so the long-press Reset shortcut
// exercises the real Replace / Merge import flow instead of a JS confirm.
export function openDevImport(): void {
  const DAY = 86_400_000;
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const at = (offsetDays: number, hh = 0, mm = 0): Date =>
    new Date(midnight.getTime() + offsetDays * DAY + hh * 3_600_000 + mm * 60_000);
  // All-day events are anchored to UTC midnight (matching AddEventModal / ICS import),
  // so the UTC-based date formatter renders a single-day span as one date, not two.
  const today = new Date();
  const atDay = (offsetDays: number): Date =>
    new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() + offsetDays));

  const draft: ParsedEvent[] = [
    makeScratchpadEvent({
      title: 'Past conference', start: atDay(-42), end: atDay(-39), allDay: true,
      location: 'Berlin', description: 'Three-day event well before today.', category: 'events',
    }),
    makeScratchpadEvent({
      title: 'Spring break', start: atDay(-10), end: atDay(-3), allDay: true,
      location: 'Crete', description: 'Week-long multi-day all-day span.', category: 'holidays',
    }),
    makeScratchpadEvent({
      title: 'Dentist', start: at(-14, 9, 0), end: at(-14, 10, 0), allDay: false,
      location: 'Clinic', category: 'none',
    }),
    makeScratchpadEvent({
      title: 'Project deadline', start: atDay(-3), end: atDay(-2), allDay: true,
      location: 'Remote', category: 'announcements',
    }),
    makeScratchpadEvent({
      title: 'Standup', start: at(-1, 9, 30), end: at(-1, 10, 0), allDay: false,
      location: 'Office', category: 'none',
    }),
    makeScratchpadEvent({
      title: 'Today all-day', start: atDay(0), end: atDay(1), allDay: true,
      location: 'Town hall', category: 'observances',
    }),
    makeScratchpadEvent({
      title: 'Lunch with Alex', start: at(0, 12, 0), end: at(0, 13, 0), allDay: false,
      location: 'Cafe', description: 'Overlaps the morning block.', category: 'guests',
    }),
    makeScratchpadEvent({
      title: 'Morning workshop', start: at(0, 11, 30), end: at(0, 12, 30), allDay: false,
      location: 'Room 2', description: 'Overlaps lunch on purpose to test lane stacking.',
      category: 'events',
    }),
    makeScratchpadEvent({
      title: 'Public holiday', start: atDay(3), end: atDay(4), allDay: true,
      category: 'holidays',
    }),
    makeScratchpadEvent({
      title: 'Trip abroad', start: atDay(5), end: atDay(9), allDay: true,
      location: 'Lisbon', category: 'events',
    }),
    makeScratchpadEvent({
      title: 'Company offsite', start: atDay(15), end: atDay(20), allDay: true,
      location: 'Porto', description: 'Five-day multi-day event for lane testing.',
      category: 'announcements',
    }),
    makeScratchpadEvent({
      title: 'Quarterly review', start: at(30, 14, 0), end: at(30, 15, 30), allDay: false,
      location: 'Boardroom', category: 'announcements',
    }),
  ];
  const sortedDraft = draft.sort((a, b) => a.start.getTime() - b.start.getTime());

  const imported: ParsedEvent[] = [
    makeScratchpadEvent({
      title: 'Imported: kickoff', start: atDay(-7), end: atDay(-6), allDay: true,
      location: 'HQ', category: 'events',
    }),
    makeScratchpadEvent({
      title: 'Imported: call', start: at(-1, 16, 0), end: at(-1, 16, 30), allDay: false,
      location: 'Zoom',
    }),
    makeScratchpadEvent({
      title: 'Imported: release', start: atDay(2), end: atDay(4), allDay: true,
      category: 'announcements',
    }),
    makeScratchpadEvent({
      title: 'Imported: retro', start: at(12, 10, 0), end: at(12, 11, 0), allDay: false,
      location: 'Room 5',
    }),
    // A run of the same event on consecutive days — should collapse into one
    // continuous bar with a ×N count on every zoom except 1W. Day 7 starts 30m
    // late and day 8 ends 30m early — within the ±1-hour merge tolerance — so
    // the merged pill shows a "10:00/10:30 — 19:30/20:00" time range.
    makeScratchpadEvent({ title: 'Imported: rehearsal', start: at(5, 10, 0), end: at(5, 20, 0), allDay: false, location: 'Stage' }),
    makeScratchpadEvent({ title: 'Imported: rehearsal', start: at(6, 10, 0), end: at(6, 20, 0), allDay: false, location: 'Stage' }),
    makeScratchpadEvent({ title: 'Imported: rehearsal', start: at(7, 10, 30), end: at(7, 20, 0), allDay: false, location: 'Stage' }),
    makeScratchpadEvent({ title: 'Imported: rehearsal', start: at(8, 10, 0), end: at(8, 19, 30), allDay: false, location: 'Stage' }),
    makeScratchpadEvent({ title: 'Imported: rehearsal', start: at(9, 10, 0), end: at(9, 20, 0), allDay: false, location: 'Stage' }),
    makeScratchpadEvent({ title: 'Imported: rehearsal', start: at(10, 10, 0), end: at(10, 20, 0), allDay: false, location: 'Stage' }),
    // Day 11 is skipped: the gap must split the run into a second, separate bar.
    makeScratchpadEvent({ title: 'Imported: rehearsal', start: at(13, 10, 0), end: at(13, 20, 0), allDay: false, location: 'Stage' }),
    makeScratchpadEvent({ title: 'Imported: rehearsal', start: at(14, 10, 0), end: at(14, 20, 0), allDay: false, location: 'Stage' }),
  ];

  // Demo filters that exercise every rule mode against the events above (the same
  // shapes a shared config's Replace/Merge import would bring in).
  const demoRules: FindReplaceRule[] = [
    // Matte: highlight + colour, text unchanged ("Morning workshop" stays as-is).
    { id: 'demo-matte', find: 'workshop', style: 'bold', category: 'none', color: 'amber' },
    // Matte + block: a full-width band with a hatch, text unchanged ("Company offsite").
    { id: 'demo-block', find: 'offsite', style: 'muted', category: 'none', color: 'sky', block: 'global' },
    // Replace with text: rewrite the matched title ("Quarterly review" → "Q3 planning").
    { id: 'demo-replace', find: 'Quarterly review', replace: 'Q3 planning', style: 'none', category: 'none' },
    // Replace with blank: strip a leading prefix from the imported lane ("Imported: ").
    { id: 'demo-blank', find: 'Imported: ', replace: '', style: 'none', category: 'none', position: 'start' },
  ];

  // Hand it all to the share-import dialog (Replace / Merge / Cancel). Replace
  // gives a clean default + demo setup; Merge adds the demo filters + lanes onto
  // the current config. The default holiday feeds ride along so Replace doesn't
  // wipe them.
  const base = defaultConfig();
  ui.shareImport = {
    feeds: base.feeds.filter((f) => f.source.kind === 'user'),
    rules: [...base.rules, ...demoRules],
    localFeeds: [
      { name: 'Draft', category: 'none', isDraft: true, hidden: false, events: sortedDraft },
      { name: 'Imported (test)', category: 'none', events: imported },
    ],
    view: null,
    kioskPin: null,
    truncated: false,
  };
}

// `lastNonWeek` remembers the zoom to return to when the 1W view is toggled off
// (the week view sits outside the normal zoom progression).
export const zoom = $state<{ value: Zoom; lastNonWeek: Zoom }>({
  value: 'month',
  lastNonWeek: 'month',
});

// Shared toolbar geometry the 1W grid reads to line its frozen timezone gutter
// up with the toolbar above it. `weekBtnLeft` is the viewport-x of the 1W
// button's left edge (0 until measured); the grid sizes its gutter so its right
// border falls on that line, holding across spacing/date-width changes.
// `zoomNavRight` is the viewport-x of the zoom nav's right edge (the 6M button's,
// or the rightmost expanded one): the search field's right edge, and the
// timeline's anchor on a wide viewport until the gap below is measured.
// `weekGapMid` is the viewport-x mid-way between the 1W button's right edge and
// the 1M button's left edge — where the timeline parks the focused date at every
// width. 0 until measured — readers fall back.
export const layout = $state<{ weekBtnLeft: number; zoomNavRight: number; weekGapMid: number }>({
  weekBtnLeft: 0,
  zoomNavRight: 0,
  weekGapMid: 0,
});

export const search = $state<{
  query: string;
  currentIndex: number;
  open: boolean;
  includesPast: boolean;
}>({
  query: '',
  currentIndex: 0,
  open: false,
  includesPast: false,
});

export const focus = $state<{ feedId: string | null; eventIndex: number }>({
  feedId: null,
  eventIndex: -1,
});

// A focused row inks its rules for a moment, then lets them fade back to the
// grid's grey: a highlight left on (a touch tap has no pointer-leave; a mouse
// or keyboard can rest on a row for minutes) read as that row being darker.
// Separate from `focus`, which stays — prev/next nav and keyboard moves keep
// working after the ink fades. Lit on every change of row focus (App), on a
// header tap or hover (which may re-focus the same row), and when DOM focus
// enters a row (Tab).
export const ROW_INK_FADE_MS = 3000;
export const rowInk = $state<{ feedId: string | null }>({ feedId: null });
let rowInkTimer: ReturnType<typeof setTimeout> | null = null;
export function lightRowInk(feedId: string | null): void {
  if (rowInkTimer != null) clearTimeout(rowInkTimer);
  rowInkTimer = null;
  rowInk.feedId = feedId;
  if (feedId == null) return;
  rowInkTimer = setTimeout(() => {
    rowInkTimer = null;
    rowInk.feedId = null;
  }, ROW_INK_FADE_MS);
}

export const selection = $state<{ mode: boolean; uids: Set<string> }>({
  mode: false,
  uids: new Set(),
});

export function toggleSelected(uid: string): void {
  const next = new Set(selection.uids);
  if (next.has(uid)) next.delete(uid);
  else next.add(uid);
  selection.uids = next;
  if (next.size === 0) selection.mode = false;
}

// Add many events at once (search's "select all matches") and open selection.
export function selectEvents(uids: Iterable<string>): void {
  const next = new Set(selection.uids);
  for (const uid of uids) next.add(uid);
  if (next.size === 0) return;
  selection.uids = next;
  selection.mode = true;
}

export function addToSelection(uid: string): void {
  if (selection.uids.has(uid)) return;
  selection.uids = new Set(selection.uids).add(uid);
}

export function clearSelection(): void {
  selection.uids = new Set();
  selection.mode = false;
}

export type LogEntry = {
  id: string;
  ts: number;
  message: string;
  kind: 'info' | 'warn' | 'error';
};

export const ui = $state<{
  modalEvent: DisplayEvent | null;
  // Lightweight hover preview (mouse only): the event under the pointer and the
  // hovered pill's viewport rect used to anchor the popover. Distinct from
  // modalEvent (the full, click-opened dialog) so the two never fight.
  hoverEvent: DisplayEvent | null;
  hoverAnchor: DOMRect | null;
  addEventOpen: boolean;
  addEventEditUid: string | null;
  // The event card an edit was opened from: Cancel, Close or the back
  // gesture return to it (Save and Delete don't, the card would be stale).
  addEventReturn: DisplayEvent | null;
  // The event card's raw iCal view is open: Escape / back steps out of it to
  // the card before closing the card.
  modalShowSource: boolean;
  // An instant to prefill the Add-event modal with (set from an empty 1W slot,
  // read on the grid's display zone); opens a timed event at that day + time.
  addEventPrefillStartMs: number | null;
  // The end a drag down the 1W grid picked (else the draft is an hour long).
  addEventPrefillEndMs: number | null;
  // The local lane a new event should land in (set by a feed row's + button);
  // null defaults to Draft. Cleared when the modal closes.
  addEventFeedId: string | null;
  settingsOpen: boolean;
  settingsScrollToFeedId: string | null;
  settingsAutoEditFeedId: string | null;
  settingsScrollToRuleId: string | null;
  settingsAutoEditRuleId: string | null;
  loading: boolean;
  error: string | null;
  // feedId (when the error is a feed's) lets the modal say when it last loaded.
  // title overrides the default "Failed to load <feedName>" heading (non-feed errors).
  errorModal: { feedId?: string; feedName: string; message: string; title?: string } | null;
  log: LogEntry[];
  statusExpanded: boolean;
  // Bumped on every font-size change: the status bar flashes the new size
  // ("100%" at the default, else "16px"…), keyed on it so a repeat restarts
  // the fade. 0 = no change this session.
  fontSizeFlash: number;
  feedErrors: Record<string, string>;
  // Feeds with a fetch in flight right now (id → true), for per-row spinners.
  loadingFeeds: Record<string, true>;
  shareImport: DecodedShare | null;
  rawEventUid: string | null;
  tempMarkerMs: number | null;
  // Optional END of a duration marker: the UTC-midnight ms of the LAST day,
  // inclusive. null = the marker is a single day. Always write both fields
  // through setTempMarkerDay / setTempMarkerRange / clearTempMarker so an end
  // never outlives its start (or drifts left of it).
  tempMarkerEndMs: number | null;
  // Which position the today↔marker cycle is currently on, so the toolbar date
  // button can show today's or the marker's date. Flipped by the cycle toggle;
  // set to 'marker' when a marker is placed/moved, 'today' when cleared/jumped.
  markerFocus: 'today' | 'marker';
  kioskPinModal: 'set' | 'unlock' | null;
  // Bumped by an imported share link: a locked kiosk reads its home view again
  // (kiosk-idle.ts), even when the link kept the same PIN.
  kioskHomeRev: number;
  shortcutsOpen: boolean;
  whatsNewOpen: boolean;
  timelineMusic: boolean;
  musicSweeping: boolean;
  // What the 'shake' Flavor/Scheme modes currently resolve to. Session-only and
  // deliberately unpersisted: config.palette/config.scheme store the *mode*, and
  // the roll below is the resolved value, the same way 'auto' resolves through
  // matchMedia. Null means "armed but not rolled yet" — rollShake() fills it.
  shakePalette: PaletteFlavor | null;
  shakeScheme: ResolvedScheme | null;
}>({
  modalEvent: null,
  hoverEvent: null,
  hoverAnchor: null,
  addEventOpen: false,
  addEventEditUid: null,
  addEventReturn: null,
  modalShowSource: false,
  addEventPrefillStartMs: null,
  addEventPrefillEndMs: null,
  addEventFeedId: null,
  settingsOpen: false,
  settingsScrollToFeedId: null,
  settingsAutoEditFeedId: null,
  settingsScrollToRuleId: null,
  settingsAutoEditRuleId: null,
  loading: false,
  error: null,
  errorModal: null,
  log: [],
  statusExpanded: false,
  fontSizeFlash: 0,
  feedErrors: {},
  loadingFeeds: {},
  shareImport: null,
  rawEventUid: null,
  tempMarkerMs: null,
  tempMarkerEndMs: null,
  markerFocus: 'today',
  kioskPinModal: null,
  kioskHomeRev: 0,
  shortcutsOpen: false,
  whatsNewOpen: false,
  timelineMusic: false,
  musicSweeping: false,
  shakePalette: null,
  shakeScheme: null,
});

// A dialog, Settings or the shortcuts sheet is open: the views' own key
// handlers stand down so keys reach (or stay with) that layer.
export function overlayOpen(): boolean {
  return !!(
    ui.modalEvent || ui.addEventOpen || ui.shareImport || ui.errorModal || ui.kioskPinModal ||
    ui.whatsNewOpen || ui.settingsOpen || ui.shortcutsOpen
  );
}

// Closes every overlay overlayOpen() counts but the share-import dialog, whose
// dismissal also strips the link from the address: closeShareImport does both.
export function closeOverlays(): void {
  ui.modalShowSource = false;
  ui.modalEvent = null;
  ui.addEventOpen = false;
  ui.errorModal = null;
  ui.kioskPinModal = null;
  ui.whatsNewOpen = false;
  ui.settingsOpen = false;
  ui.shortcutsOpen = false;
}

/** Whether the UI's font size may change here: not on a locked kiosk. */
export function canResizeUi(): boolean {
  return !isKiosk();
}

/** Set the UI's font size. Each change flashes the new size in the status bar;
 *  arriving back at the default reads "100%" with a detent felt through the
 *  Haptics setting, so the home size is findable mid-pinch. A locked kiosk keeps
 *  its size: visitors can't change the device's. */
export function setFontSize(size: FontSize): void {
  if (!canResizeUi() || size === config.fontSize) return;
  config.fontSize = size;
  ui.fontSizeFlash++;
  if (size === DEFAULT_FONT_SIZE) detent();
}

/** The status bar's size readout: "100%" at the default, else the size in px. */
export function fontSizeLabel(size: FontSize): string {
  return size === DEFAULT_FONT_SIZE ? '100%' : `${size}px`;
}

/** One step along FONT_SIZES (+1 larger, −1 smaller), held at either end. */
export function stepFontSize(dir: 1 | -1): void {
  const now = config.fontSize;
  // The next size up or down, so even a size off the list steps the right way.
  const next = dir > 0 ? FONT_SIZES.find((s) => s > now) : FONT_SIZES.filter((s) => s < now).pop();
  if (next != null) setFontSize(next);
}

// Back to today, the one way every control gets there: the toolbar's date and
// zoom buttons (double tap), 1W's double tap, `0`, Space twice, and a kiosk's
// idle return. Drops the keyboard focus; `clearMarker` also clears the marker.
// A zoom change carries the jump itself, so the timeline scrolls once; 1W
// hears the jump on its own (its zoom ignores it).
export function goToToday(opts: { zoom?: Zoom; clearMarker?: boolean } = {}): void {
  focus.feedId = null;
  focus.eventIndex = -1;
  if (opts.clearMarker) clearTempMarker();
  const target = opts.zoom;
  const switching = target != null && target !== zoom.value;
  if (switching) {
    window.dispatchEvent(new CustomEvent('cal:set-zoom', { detail: { zoom: target, jumpToday: true } }));
  }
  if (!switching || target === 'week') window.dispatchEvent(new CustomEvent('cal:jump-today'));
}

// --- Temporary day marker ---------------------------------------------------
// The marker is either a single UTC-midnight day (tempMarkerMs) or a duration:
// that day plus an inclusive last day (tempMarkerEndMs). Every writer goes
// through these three so the pair can never end up inconsistent — a plain
// placement always collapses a previous range, and the end is clamped to the
// start rather than allowed to cross it.

export function setTempMarkerDay(ms: number): void {
  ui.tempMarkerMs = ms;
  ui.tempMarkerEndMs = null;
}

export function setTempMarkerRange(startMs: number, endMs: number): void {
  ui.tempMarkerMs = startMs;
  ui.tempMarkerEndMs = Math.max(startMs, endMs);
}

export function clearTempMarker(): void {
  ui.tempMarkerMs = null;
  ui.tempMarkerEndMs = null;
}

// A double-tap that clears the marker on its pointerup unmounts the line under
// the finger, and the tap's synthesized click then lands on the grid beneath —
// whose tap-to-place put the marker straight back. Touch only: Chrome doesn't
// dispatch a mouse click whose pressed element vanished. Gesture clears go
// through here, and the tap-to-place handlers skip that one trailing click.
const TRAILING_CLICK_MS = 500;
let markerClearedByTapAt = 0;
export function clearTempMarkerByTap(): void {
  clearTempMarker();
  markerClearedByTapAt = Date.now();
}
export function isTrailingClearClick(): boolean {
  return Date.now() - markerClearedByTapAt < TRAILING_CLICK_MS;
}

// The marker as a resolved span: endMs is the INCLUSIVE last day (== startMs for
// a single-day marker) and days the inclusive day count. The one read helper the
// timeline, the 1W grid, the header labels and the tray share.
export function markerRange(): { startMs: number; endMs: number; days: number } | null {
  const startMs = ui.tempMarkerMs;
  if (startMs == null) return null;
  const endMs = Math.max(startMs, ui.tempMarkerEndMs ?? startMs);
  return { startMs, endMs, days: Math.round((endMs - startMs) / MS_PER_DAY) + 1 };
}

// The marker covers more than one day. A duration resized down to a single day
// keeps its end edge (so the drag that shrank it carries on), but reads and
// behaves as a single-day marker everywhere it's shown: no count, no range
// label, no visible end line, the tray's default window.
export function markerIsSpan(): boolean {
  const r = markerRange();
  return r != null && r.days > 1;
}

// Roll the appearance axes that are in 'shake' mode, and clear the roll for any
// that aren't so switching away and back re-rolls. `force` is a real shake (or
// any deliberate re-roll); without it this only fills in axes that have no value
// yet, which is what arms the mode on launch and the moment "Shake" is picked.
// Axes left on a concrete Flavor/Scheme are never touched — an explicit choice
// always wins.
export function rollShake(force = false): boolean {
  let rolled = false;
  if (config.palette === 'shake') {
    if (force || ui.shakePalette === null) {
      ui.shakePalette = pickRandom(PALETTE_FLAVORS, ui.shakePalette);
      rolled = true;
    }
  } else if (ui.shakePalette !== null) {
    ui.shakePalette = null;
  }
  if (config.scheme === 'shake') {
    if (force || ui.shakeScheme === null) {
      ui.shakeScheme = pickRandom(RESOLVED_SCHEMES, ui.shakeScheme);
      rolled = true;
    }
  } else if (ui.shakeScheme !== null) {
    ui.shakeScheme = null;
  }
  return rolled;
}

// True when at least one axis is in 'shake' mode, i.e. worth listening for
// device motion at all.
export function shakeArmed(): boolean {
  return config.palette === 'shake' || config.scheme === 'shake';
}

// The concrete values that reach data-palette / data-scheme. 'shake' resolves to
// the current roll; 'auto' scheme falls through to the OS preference. Callers
// pass `prefersDark` — read it from `viewport` (src/lib/viewport.svelte.ts),
// which owns the single matchMedia listener; components never call matchMedia.
export function displayPalette(): PaletteFlavor {
  if (config.palette === 'shake') return ui.shakePalette ?? DEFAULT_PALETTE_FLAVOR;
  if (config.palette === 'auto') return DEFAULT_PALETTE_FLAVOR;
  return config.palette;
}

export function displayScheme(prefersDark: boolean): ResolvedScheme {
  if (config.scheme === 'shake') return ui.shakeScheme ?? (prefersDark ? 'dark' : 'light');
  if (config.scheme === 'auto') return prefersDark ? 'dark' : 'light';
  return config.scheme;
}

// Kiosk mode is active iff a PIN exists. Reading the reactive config field keeps
// callers (templates, deriveds, effects) updated when the PIN is set/cleared.
export function isKiosk(): boolean {
  return config.kioskPin != null;
}

// --- Lightweight hover preview (mouse-only) ---------------------------------
// Debounced open/close intent so skimming the mouse across dense pills doesn't
// flash the preview. The card stays mounted while it swaps between events, so
// moving from one pill to the next never closes and reopens it.
const HOVER_OPEN_MS = 120;
const HOVER_CLOSE_MS = 90;
let hoverOpenTimer: ReturnType<typeof setTimeout> | null = null;
let hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;

function clearHoverOpen(): void {
  if (hoverOpenTimer != null) { clearTimeout(hoverOpenTimer); hoverOpenTimer = null; }
}
function clearHoverClose(): void {
  if (hoverCloseTimer != null) { clearTimeout(hoverCloseTimer); hoverCloseTimer = null; }
}

/** Open (or, if already open, swap to) the hover preview for `event`. */
export function openHoverPreview(event: DisplayEvent, anchor: DOMRect): void {
  // Never compete with the full click-opened modal or bulk-selection mode.
  if (ui.modalEvent || selection.mode) return;
  clearHoverClose();
  // Already visible → swap content instantly (no close→reopen flash).
  if (ui.hoverEvent) {
    ui.hoverEvent = event;
    ui.hoverAnchor = anchor;
    return;
  }
  clearHoverOpen();
  hoverOpenTimer = setTimeout(() => {
    hoverOpenTimer = null;
    if (ui.modalEvent || selection.mode) return;
    ui.hoverEvent = event;
    ui.hoverAnchor = anchor;
  }, HOVER_OPEN_MS);
}

/** Close the preview after a short grace period (cancelled if a pill re-opens). */
export function closeHoverPreviewSoon(): void {
  clearHoverOpen();
  clearHoverClose();
  hoverCloseTimer = setTimeout(() => {
    hoverCloseTimer = null;
    ui.hoverEvent = null;
    ui.hoverAnchor = null;
  }, HOVER_CLOSE_MS);
}

/** Close the preview immediately (e.g. when a click opens the full modal). */
export function cancelHoverPreview(): void {
  clearHoverOpen();
  clearHoverClose();
  ui.hoverEvent = null;
  ui.hoverAnchor = null;
}

const MAX_LOG_ENTRIES = 50;

export function pushLog(message: string, kind: LogEntry['kind'] = 'info'): void {
  const entry: LogEntry = {
    id: Math.random().toString(36).slice(2, 10) + ts(),
    ts: Date.now(),
    message,
    kind,
  };
  ui.log = [entry, ...ui.log].slice(0, MAX_LOG_ENTRIES);
}

function ts(): string {
  return Date.now().toString(36);
}

// Per-feed events with find/replace rules applied. Events are never hidden by
// their start clock time — morningLimit/eveningLimit only drive the working-hours
// visuals (day/night tint, 1W grid window), not visibility. A rule can still set
// `hidden` for the 'hidden' style variant (see applyRules), which renders as a
// faint placeholder rather than dropping the event.
// Drop events that repeat a uid within a feed. Feed occurrences already carry a
// composite `uid:startMs` id, so this only collapses genuine duplicates — a feed
// with two identical VEVENTs, or a scratchpad lane that ended up with a repeated
// uid. Without it those repeats become duplicate keys in the timeline's keyed
// {#each} blocks (Svelte throws `each_key_duplicate`, blanking the view — which
// is why switching into the horizontal timeline could crash).
function dedupeByUid(list: DisplayEvent[]): DisplayEvent[] {
  const seen = new Set<string>();
  const out: DisplayEvent[] = [];
  for (const e of list) {
    if (seen.has(e.uid)) continue;
    seen.add(e.uid);
    out.push(e);
  }
  return out;
}

// Per-feed decoration cache keyed on the identity of the feed's raw event array
// and the rules array. Decoration (applyRules + dedupeByUid) is O(events × rules)
// per feed, and `events.byFeed` / `config.rules` mutate one slice at a time — so
// caching per feed means editing one feed (or refreshing one network feed) only
// re-decorates that feed, not every feed. Mirrors Timeline's `sortedFor`
// identity-cache. The cache is keyed by feedId and invalidated by reference, so a
// feed whose array and the rules are both unchanged reuses its DisplayEvent[]
// (stable reference — which also lets downstream identity-keyed memos stay warm).
const _decorateCache = new Map<
  string,
  { evRef: ParsedEvent[] | undefined; rulesRef: FindReplaceRule[]; result: DisplayEvent[] }
>();
// Second layer keyed on the decorated array's identity + the window bounds, so the
// window filter reuses the same array while the window is stable (keeps downstream
// identity memos — _mergeCache, Timeline's sortedFor — warm) and only re-filters
// when the decorated events or the past/future limits change. Kept separate from
// _decorateCache because decoration (applyRules) is window-independent: changing
// the limits must not force a full re-decoration of every feed.
const _windowCache = new Map<
  string,
  { srcRef: DisplayEvent[]; startMs: number; endMs: number; result: DisplayEvent[] }
>();
// Local lanes store a repeating event once; expand each series into its
// occurrences over the window, cached on the lane array + window so the
// decoration cache below stays keyed on a stable array. A lane with no series
// passes through as the same array.
const _expandCache = new Map<
  string,
  { srcRef: ParsedEvent[] | undefined; startMs: number; endMs: number; result: ParsedEvent[] | undefined }
>();
function expandedLane(feedId: string, src: ParsedEvent[] | undefined, startMs: number, endMs: number): ParsedEvent[] | undefined {
  if (!src || !feedId.startsWith('scratchpad:')) return src;
  const c = _expandCache.get(feedId);
  if (c && c.srcRef === src && c.startMs === startMs && c.endMs === endMs) return c.result;
  const result = expandLaneEvents(src, startMs, endMs);
  _expandCache.set(feedId, { srcRef: src, startMs, endMs, result });
  return result;
}

const _displayByFeed = $derived.by<Record<string, DisplayEvent[]>>(() => {
  const out: Record<string, DisplayEvent[]> = {};
  const rules = config.rules;
  const { start, end } = _displayRange;
  const startMs = start.getTime();
  const endMs = end.getTime();
  const liveIds = new Set<string>();
  for (const feed of config.feeds) {
    liveIds.add(feed.id);
    const evRef = expandedLane(feed.id, events.byFeed[feed.id], startMs, endMs);
    const cached = _decorateCache.get(feed.id);
    let decorated: DisplayEvent[];
    if (cached && cached.evRef === evRef && cached.rulesRef === rules) {
      decorated = cached.result;
    } else {
      decorated = dedupeByUid(applyRules(evRef ?? [], rules));
      _decorateCache.set(feed.id, { evRef, rulesRef: rules, result: decorated });
    }
    // Drop events that fall entirely outside the current window so nothing renders
    // (or gets keyboard-indexed) past the timeline edges — covers cached events
    // left from a wider window and local-lane events never re-parsed against it.
    const wc = _windowCache.get(feed.id);
    if (wc && wc.srcRef === decorated && wc.startMs === startMs && wc.endMs === endMs) {
      out[feed.id] = wc.result;
    } else {
      const windowed = decorated.filter((e) => overlapsWindow(e, startMs, endMs));
      _windowCache.set(feed.id, { srcRef: decorated, startMs, endMs, result: windowed });
      out[feed.id] = windowed;
    }
  }
  // Drop cache entries for feeds that no longer exist so they can't grow unbounded.
  for (const id of _decorateCache.keys()) {
    if (!liveIds.has(id)) _decorateCache.delete(id);
  }
  for (const id of _windowCache.keys()) {
    if (!liveIds.has(id)) _windowCache.delete(id);
  }
  for (const id of _expandCache.keys()) {
    if (!liveIds.has(id)) _expandCache.delete(id);
  }
  return out;
});

export function displayEventsFor(feedId: string): DisplayEvent[] {
  return _displayByFeed[feedId] ?? [];
}

export function getDisplayByFeed(): Record<string, DisplayEvent[]> {
  return _displayByFeed;
}

// Per-feed events exactly as the horizontal timeline renders them: visible
// (time-limit-hidden dropped, Hidden-style kept), start-sorted, with runs of
// the same event on consecutive days merged into one continuous bar. The 1W
// week view renders per-day (via WeekGrid), so it skips the merge. This is the
// single source of truth for focus/keyboard-nav indexing so every list — the
// lane pills, arrow-key navigation, the header prev/next, and focus-by-uid —
// agrees on the same events in the same order.
// Per-feed "visible, then consecutive-day-merged" list, memoized on the identity
// of the feed's decorated array (stable across zoom thanks to `_displayByFeed`'s
// per-feed cache) plus the effective timezone. mergeConsecutiveDays is the
// expensive step and was being recomputed independently by timelineEventsFor,
// RowHeader, App, EventModal, focusEventByUid AND Timeline — this collapses them
// onto one cached pass per feed. Returns a deterministic start-sorted result.
const _mergeCache = new Map<
  string,
  { srcRef: DisplayEvent[]; tz: string; byLocation: boolean; result: DisplayEvent[] }
>();
export function mergedVisibleFor(feedId: string): DisplayEvent[] {
  const src = _displayByFeed[feedId] ?? [];
  const tz = effectiveFeedTz(feedId) ?? config.timezone;
  // A row split by location keeps each location's days apart (one bar per band).
  const byLocation = config.feeds.some((f) => f.id === feedId && f.subLanes === 'location');
  const cached = _mergeCache.get(feedId);
  if (cached && cached.srcRef === src && cached.tz === tz && cached.byLocation === byLocation) return cached.result;
  const visible = src.filter((e) => !e.hidden || e.styleVariant === 'hidden');
  const result = mergeConsecutiveDays(visible, tz, byLocation);
  _mergeCache.set(feedId, { srcRef: src, tz, byLocation, result });
  return result;
}

export function timelineEventsFor(feedId: string): DisplayEvent[] {
  if (zoom.value === 'week') {
    // filter() already copies, so sort it in place.
    return (_displayByFeed[feedId] ?? [])
      .filter((e) => !e.hidden || e.styleVariant === 'hidden')
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }
  // mergeConsecutiveDays returns a deterministic start-sorted result.
  return mergedVisibleFor(feedId);
}

// Move keyboard/visual focus to an event by uid, finding its feed and the
// index within that feed's rendered list (via timelineEventsFor) — matching
// the indexing Row uses for both expanded pills and collapsed dots.
export function focusEventByUid(uid: string): void {
  for (const feed of config.feeds) {
    const idx = timelineEventsFor(feed.id).findIndex((e) => e.uid === uid);
    if (idx >= 0) {
      focus.feedId = feed.id;
      focus.eventIndex = idx;
      return;
    }
  }
}

export function effectiveFeedTz(feedId: string): string | null {
  const feed = config.feeds.find((f) => f.id === feedId);
  if (feed?.timezone && feed.timezone.length > 0) return feed.timezone;
  return events.tzByFeed[feedId] ?? null;
}
